import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

const APP_NAME = "Click Bloom";

type Flower = {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  delay: number;
};

const COLORS = ["#FF6B6B", "#FFB8B8", "#FFD93D", "#6BCB77", "#4D96FF", "#B89BFF", "#FF9BFF"];

function App() {
  const [flowers, setFlowers] = useState<Flower[]>([]);

  const spawnFlower = (x: number, y: number) => {
    const newFlower: Flower = {
      id: Date.now() + Math.random(),
      x,
      y,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      size: 20 + Math.random() * 30,
      delay: 0,
    };
    setFlowers((prev) => [...prev, newFlower]);
  };

  const handleClick = (e: React.MouseEvent) => {
    spawnFlower(e.clientX, e.clientY);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      spawnFlower(Math.random() * window.innerWidth, Math.random() * window.innerHeight);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setFlowers((prev) => prev.slice(-50));
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="application"
      aria-label="Flower blooming area"
      style={{
        fontFamily: "system-ui, sans-serif",
        height: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f0fdf4",
        cursor: "crosshair",
        userSelect: "none",
        outline: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "2rem",
          left: "2rem",
          pointerEvents: "none",
          color: "#166534",
          zIndex: 10,
        }}
      >
        <h1 style={{ margin: 0 }}>{APP_NAME}</h1>
        <p>Click anywhere to plant a flower 🌸</p>
      </div>

      {flowers.map((flower) => (
        <div
          key={flower.id}
          style={{
            position: "absolute",
            left: flower.x,
            top: flower.y,
            width: flower.size,
            height: flower.size,
            backgroundColor: flower.color,
            borderRadius: "50%",
            transform: "translate(-50%, -50%) scale(0)",
            animation: "bloom 0.6s ease-out forwards",
            boxShadow: `0 0 15px ${flower.color}66`,
            zIndex: 1,
            filter: "blur(1px)",
          }}
        />
      ))}

      <style>{`
        @keyframes bloom {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          60% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Missing #root element");
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
